import { formatFieldDisplayName } from './vizUtils'
import type {
  AgentBuilderCatalogView,
  AgentBuilderFieldOption,
  BuilderFieldOption,
  DashboardCatalogPlatform,
  DashboardDefinition,
  DashboardSlicerPanel,
  DashboardWidget,
  DashboardWidgetFilter,
  DashboardWidgetType,
} from './types'

export const DEFAULT_PLATFORM = 'Current Client'
export const DASHBOARD_VERSION = 'dashboard-builder-agent.v2'
const LEGACY_DASHBOARD_VERSION = 'dashboard-builder-agent.v1'

const EDITABLE_WIDGET_TYPES: DashboardWidgetType[] = ['kpi', 'bar', 'groupedBar', 'line', 'donut', 'gauge', 'table', 'dataBarTable', 'pivotTable']
const TYPE_LABELS: Record<DashboardWidgetType, string> = {
  kpi: 'KPI',
  bar: 'Bar',
  groupedBar: 'Grouped Bar',
  line: 'Line',
  donut: 'Donut',
  gauge: 'Gauge',
  table: 'Table',
  dataBarTable: 'Data Bar Table',
  pivotTable: 'Pivot Table',
  insight: 'Insight',
}

const TYPE_SIZES: Record<DashboardWidgetType, 'half' | 'full'> = {
  kpi: 'full',
  bar: 'half',
  groupedBar: 'half',
  line: 'half',
  donut: 'half',
  gauge: 'half',
  table: 'full',
  dataBarTable: 'full',
  pivotTable: 'full',
  insight: 'full',
}

type WidgetLayoutInput = Pick<DashboardWidget, 'type'> & Partial<Pick<DashboardWidget, 'size' | 'width'>>

export function resolveWidgetWidthPercent(widget: WidgetLayoutInput | null | undefined) {
  const rawWidth = Number(widget?.width)
  if (Number.isFinite(rawWidth) && rawWidth > 0) {
    return Math.min(100, Math.max(1, rawWidth))
  }

  if (widget?.size === 'full') return 100
  if (widget?.size === 'half') return 50
  return TYPE_SIZES[widget?.type ?? 'bar'] === 'full' ? 100 : 50
}

export function resolveWidgetWidthCssValue(widget: WidgetLayoutInput | null | undefined) {
  return `${resolveWidgetWidthPercent(widget)}%`
}

function deriveWidgetSize(widget: WidgetLayoutInput) {
  return resolveWidgetWidthPercent(widget) >= 100 ? 'full' : 'half'
}

const INTENTS = [
  {
    key: 'wip',
    title: 'WIP Aging Overview',
    summary: 'Highlights overdue WIP exposure, where it sits, and which jobs need follow-up.',
    keywords: ['wip', 'aging', 'aged', 'overdue', 'write off', 'write-off', 'recovery'],
    dimensions: ['Clients[Client Manager]', 'Jobs[Job Manager]', 'Clients[Client Name]', 'Jobs[Name]', 'Date[Date]'],
    measures: ['WIP Balance', 'Aged WIP 91+', 'WIP Open', 'Aged WIP 61-90', 'Aged WIP 31-60', 'Aged WIP 0-30'],
    widgetPlan: ['kpi', 'groupedBar', 'bar', 'table', 'insight'] as DashboardWidgetType[],
  },
  {
    key: 'billing',
    title: 'Billing Performance',
    summary: 'Compares current billing, trend, mix, and client-level performance.',
    keywords: ['billing', 'billings', 'revenue', 'invoice', 'invoicing', 'debtors'],
    dimensions: ['Date[Date]', 'Clients[Client Group]', 'Clients[Client Name]', 'Clients[Client Partner]'],
    measures: ['Billing', 'Billing LY', 'Billing Target', 'Average per Client', 'Average per Client Group'],
    widgetPlan: ['kpi', 'line', 'donut', 'table', 'insight'] as DashboardWidgetType[],
  },
  {
    key: 'profitability',
    title: 'Profitability and Recovery',
    summary: 'Shows margin, recovery, mix, and the areas most likely to need action.',
    keywords: ['profit', 'profitability', 'margin', 'recoverability', 'recovery', 'write ons', 'write offs'],
    dimensions: ['Clients[Client Group]', 'Clients[Client Name]', 'Jobs[Job Category]', 'Jobs[Job Partner]', 'Date[Date]'],
    measures: ['Profitability', 'Profitability %', 'Recoverability %', 'Recoverability Target', 'Write Ons/Offs'],
    widgetPlan: ['kpi', 'groupedBar', 'donut', 'table', 'insight'] as DashboardWidgetType[],
  },
  {
    key: 'staff',
    title: 'Staff Utilisation',
    summary: 'Reviews workload, productivity, target attainment, and where capacity is uneven.',
    keywords: ['staff', 'team', 'utilisation', 'utilization', 'capacity', 'timesheet', 'productivity', 'hours'],
    dimensions: ['Staff[Name]', 'Staff[Team]', 'Staff Team Manager[Team Manager]', 'Date[Date]'],
    measures: ['Production', 'Billable Hours', 'Non-Billable Hours', 'Capacity Hours', 'Productivity %', 'Productivity Target'],
    widgetPlan: ['kpi', 'groupedBar', 'bar', 'table', 'insight'] as DashboardWidgetType[],
  },
  {
    key: 'jobs',
    title: 'Job Pipeline',
    summary: 'Tracks job volume, timing, and which work items are becoming operational risk.',
    keywords: ['job', 'jobs', 'pipeline', 'due', 'turnaround', 'budget'],
    dimensions: ['Jobs[State]', 'Jobs[Job Category]', 'Jobs[Job Manager]', 'Jobs[DueDate]', 'Jobs[Name]'],
    measures: ['Open Jobs', 'Open Jobs Budget', 'Open Jobs Overdue', 'Open Job Aging', 'Completed Jobs', 'Job Turnaround Time'],
    widgetPlan: ['kpi', 'bar', 'line', 'table', 'insight'] as DashboardWidgetType[],
  },
  {
    key: 'tax',
    title: 'Tax Lodgement Status',
    summary: 'Summarises lodgement volume, due-date pattern, and the backlog needing attention.',
    keywords: ['tax', 'lodgement', 'lodgment', 'ato', 'filed', 'return'],
    dimensions: ['Income Tax Lodgement[Tax Year]', 'Income Tax Lodgement[State]', 'Income Tax Lodgement[Due Date]', 'Income Tax Lodgement[ATOClientName]'],
    measures: ['Total Lodgements', 'Total Lodgements Lodged', 'Total Lodgements Lodged on Time'],
    widgetPlan: ['kpi', 'groupedBar', 'line', 'table', 'insight'] as DashboardWidgetType[],
  },
] as const

const ORDINAL_INDEX = {
  first: 0,
  second: 1,
  third: 2,
  fourth: 3,
  fifth: 4,
} as const

function mapFieldOption(option: AgentBuilderFieldOption): BuilderFieldOption {
  return {
    name: option.name,
    value: option.value,
    type: option.type,
    isCustom: option.isCustom,
  }
}

export function buildCatalogFromAgentBuilderCatalog(
  options: AgentBuilderCatalogView,
  platformTypeId: number,
  platformTypeName = DEFAULT_PLATFORM,
): DashboardCatalogPlatform[] {
  return [{
    platformTypeId,
    platformTypeName,
    defaultDimensions: options.fieldDimensionOptions.map(mapFieldOption),
    defaultMeasures: options.fieldMeasureOptions.map(mapFieldOption),
    datePresetOptions: options.datePresetOptions ?? [],
  }]
}

export function hasWidgetHydrationPayload(widget: DashboardWidget) {
  return widget.type !== 'insight' && (widget.daxGenerationRequest?.measures?.length ?? 0) > 0
}

export function buildCatalogFromDashboardDefinition(dashboard: DashboardDefinition): DashboardCatalogPlatform[] {
  const dimensionCandidates = [
    ...(dashboard.dashboardFilters ?? []).map((filter) => ({
      name: filter.name,
      value: filter.value,
      type: filter.type,
    })),
    ...(dashboard.slicerPanel?.slicers ?? []).map((slicer) => ({
      name: slicer.label,
      value: slicer.field,
      type: 'string',
    })),
    ...(dashboard.slicerPanel?.dateRange?.field ? [{
      name: dashboard.slicerPanel.dateRange.field,
      value: dashboard.slicerPanel.dateRange.field,
      type: 'date',
    }] : []),
    ...(dashboard.widgets ?? []).flatMap((widget) => widget.dimensions ?? []),
    ...(dashboard.widgets ?? []).flatMap((widget) => (
      widget.daxGenerationRequest?.dimensions ?? []
    ).map((value) => ({
      name: value,
      value,
      type: 'string',
    }))),
  ]

  const measureCandidates = [
    ...(dashboard.widgets ?? []).flatMap((widget) => widget.measures ?? []),
    ...(dashboard.widgets ?? []).flatMap((widget) => (
      widget.daxGenerationRequest?.measures ?? []
    ).map((value) => ({
      name: value,
      value,
      type: 'number',
    }))),
  ]

  return [{
    platformTypeId: dashboard.platformTypeId,
    platformTypeName: dashboard.platformType || DEFAULT_PLATFORM,
    defaultDimensions: uniqueByValue(dimensionCandidates),
    defaultMeasures: uniqueByValue(measureCandidates),
  }]
}

export function normalizeToken(value: string | number | null | undefined) {
  return String(value ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

export function flattenCatalog(catalog: DashboardCatalogPlatform[]) {
  const platform = catalog.find((item) => item.platformTypeName === DEFAULT_PLATFORM) ?? catalog[0]
  if (!platform) {
    throw new Error('Dashboard catalog is empty.')
  }
  return {
    platformType: platform.platformTypeName,
    platformTypeId: platform.platformTypeId,
    dimensions: platform.defaultDimensions ?? [],
    measures: platform.defaultMeasures ?? [],
  }
}

export function findOption(options: BuilderFieldOption[], requestedName: string) {
  const normalized = normalizeToken(requestedName)
  return options.find((option) => normalizeToken(option.name) === normalized)
    ?? options.find((option) => normalizeToken(option.value) === normalized)
    ?? null
}

function detectIntent(query: string) {
  const q = String(query ?? '').toLowerCase()
  const scored = INTENTS.map((intent) => ({
    intent,
    score: intent.keywords.reduce((count, keyword) => count + (q.includes(keyword) ? 1 : 0), 0),
  })).sort((a, b) => b.score - a.score)

  const topMatch = scored[0]
  return topMatch && topMatch.score > 0 ? topMatch.intent : INTENTS[0]
}

function extractRequestedFields(query: string, fieldOptions: BuilderFieldOption[]) {
  const normalizedQuery = normalizeToken(query)
  return fieldOptions.filter((option) => {
    const name = normalizeToken(option.name)
    const value = normalizeToken(option.value)
    return name.length > 3 && (normalizedQuery.includes(name) || normalizedQuery.includes(value))
  })
}

function uniqueByValue<T extends { value: string }>(options: Array<T | null | undefined>) {
  const seen = new Set<string>()
  return options.filter((option): option is T => {
    if (!option || seen.has(option.value)) {
      return false
    }
    seen.add(option.value)
    return true
  })
}

function slug(value: string) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 48)
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function inferDashboardIntent(dashboard: DashboardDefinition) {
  const direct = INTENTS.find((intent) => intent.key === dashboard.intentKey)
  if (direct) {
    return direct
  }

  const titleToken = normalizeToken(dashboard.title)
  const matchedByTitle = INTENTS.find((intent) => titleToken.includes(normalizeToken(intent.title)))
  if (matchedByTitle) {
    return matchedByTitle
  }

  const widgetMeasureTokens = new Set(
    (dashboard.widgets ?? [])
      .flatMap((widget) => widget.measures ?? [])
      .map((measure) => normalizeToken(measure.name || measure.value)),
  )

  const scored = INTENTS.map((intent) => ({
    intent,
    score: intent.measures.reduce((count, measure) => count + (widgetMeasureTokens.has(normalizeToken(measure)) ? 1 : 0), 0),
  })).sort((a, b) => b.score - a.score)

  const topMatch = scored[0]
  return topMatch && topMatch.score > 0 ? topMatch.intent : INTENTS[0]
}

function isDateDimension(dimension: BuilderFieldOption) {
  return dimension?.type === 'date' || normalizeToken(dimension?.name).includes('date') || normalizeToken(dimension?.value).includes('date')
}

function selectDimensionsForType(type: DashboardWidgetType, dimensions: BuilderFieldOption[]) {
  const categorical = dimensions.filter((dimension) => !isDateDimension(dimension))
  const dated = dimensions.filter(isDateDimension)

  if (type === 'kpi' || type === 'gauge' || type === 'insight') {
    return []
  }

  if (type === 'line') {
    return dated.slice(0, 1).length ? dated.slice(0, 1) : dimensions.slice(0, 1)
  }

  if (type === 'table') {
    return uniqueByValue([
      ...categorical.slice(0, 3),
      ...dated.slice(0, 1),
    ]).slice(0, 4)
  }

  if (type === 'dataBarTable') {
    return uniqueByValue([
      ...categorical.slice(0, 2),
      ...dated.slice(0, 1),
    ]).slice(0, 3)
  }

  if (type === 'pivotTable') {
    return uniqueByValue([
      ...categorical.slice(0, 2),
      ...dated.slice(0, 1),
    ]).slice(0, 3)
  }

  if (type === 'donut') {
    return categorical.slice(0, 1).length ? categorical.slice(0, 1) : dimensions.slice(0, 1)
  }

  return categorical.slice(0, 1).length ? categorical.slice(0, 1) : dimensions.slice(0, 1)
}

function selectMeasuresForType(type: DashboardWidgetType, measures: BuilderFieldOption[]) {
  if (type === 'insight') {
    return []
  }

  if (type === 'kpi') {
    return measures.slice(0, 3)
  }

  // Gauge measures map positionally to actual / target.
  if (type === 'gauge') {
    return measures.slice(0, 2)
  }

  if (type === 'bar' || type === 'line' || type === 'donut') {
    return measures.slice(0, 1)
  }

  if (type === 'groupedBar') {
    return measures.slice(0, 3)
  }

  if (type === 'table') {
    return measures.slice(0, 4)
  }

  if (type === 'dataBarTable') {
    return measures.slice(0, 3)
  }

  if (type === 'pivotTable') {
    return measures.slice(0, 2)
  }

  return measures.slice(0, 2)
}

function buildFilter(query: string, dimensions: BuilderFieldOption[]): DashboardWidgetFilter[] {
  const dateDimension = dimensions.find(isDateDimension)
  if (!dateDimension) {
    return []
  }

  const q = String(query ?? '').toLowerCase()
  const value = q.includes('last month')
    ? 'end of last month'
    : q.includes('this month')
      ? 'end of this month'
      : q.includes('last quarter')
        ? 'end of last quarter'
        : 'today'

  return [{
    fieldName: dateDimension.name,
    fieldValue: dateDimension.value,
    type: dateDimension.type,
    operator: '<=',
    value,
  }]
}

function buildDaxGenerationRequest(dimensions: BuilderFieldOption[], measures: BuilderFieldOption[], filters: DashboardWidgetFilter[]) {
  return {
    dimensions: dimensions.map((dimension) => dimension.value),
    measures: measures.map((measure) => measure.value),
    filters: filters.map((filter) => ({
      Type: filter.type,
      Field: filter.fieldValue,
      Value: filter.value,
      Filter: filter.operator,
    })),
  }
}

function buildWidgetDescription(type: DashboardWidgetType, title: string, selectedDimensions: BuilderFieldOption[], selectedMeasures: BuilderFieldOption[]) {
  if (type === 'insight') {
    return `Narrative placeholder that explains what matters in ${title.toLowerCase()} and what to do next.`
  }

  if (type === 'table') {
    return 'Detailed rows using validated PracIntel dimensions and measures for drill-in review.'
  }

  if (type === 'dataBarTable') {
    return 'Detailed table with inline bar comparisons for fast scanning across numeric measures.'
  }

  if (type === 'pivotTable') {
    return 'Matrix-style pivot table for comparing measures across row and column dimensions.'
  }

  const measureSummary = selectedMeasures.map((measure) => formatFieldDisplayName(measure.name)).join(', ')
  const dimensionSummary = selectedDimensions.map((dimension) => formatFieldDisplayName(dimension.name)).join(', ')

  if (type === 'kpi') {
    return `Headline scorecard for ${measureSummary}.`
  }

  if (type === 'donut') {
    return `Composition view of ${measureSummary}${dimensionSummary ? ` by ${dimensionSummary}` : ''}.`
  }

  if (type === 'line') {
    return `Trend view of ${measureSummary}${dimensionSummary ? ` over ${dimensionSummary}` : ''}.`
  }

  if (type === 'groupedBar') {
    return `Side-by-side comparison of ${measureSummary}${dimensionSummary ? ` by ${dimensionSummary}` : ''}.`
  }

  return `${TYPE_LABELS[type]} view of ${measureSummary}${dimensionSummary ? ` by ${dimensionSummary}` : ''}.`
}

function buildWidgetTitle(type: DashboardWidgetType, intent: (typeof INTENTS)[number], selectedDimensions: BuilderFieldOption[], index: number) {
  const firstDimension = selectedDimensions[0]?.name
  const shortDimension = firstDimension ? firstDimension.split('[').pop()?.replace(']', '') : undefined
  const base = intent.title.replace(/\s+Overview$/, '')

  if (type === 'kpi') {
    return `${base} KPIs`
  }

  if (type === 'groupedBar') {
    return shortDimension ? `${base} by ${shortDimension}` : `${base} Comparison`
  }

  if (type === 'bar') {
    return shortDimension ? `${base} Highlights by ${shortDimension}` : `${base} Highlights`
  }

  if (type === 'line') {
    return shortDimension ? `${base} Trend by ${shortDimension}` : `${base} Trend`
  }

  if (type === 'donut') {
    return shortDimension ? `${base} Mix by ${shortDimension}` : `${base} Mix`
  }

  if (type === 'table') {
    return `${base} Detail`
  }

  if (type === 'dataBarTable') {
    return `${base} Detail Bars`
  }

  if (type === 'pivotTable') {
    return `${base} Pivot`
  }

  if (type === 'gauge') {
    return `${base} Gauge`
  }

  return `${base} Insight ${index + 1}`
}

function buildSlicerPanel(dimensions: BuilderFieldOption[], widgets: DashboardWidget[], fallbackFilters: DashboardWidgetFilter[]): DashboardSlicerPanel | null {
  const dashboardFilters = buildDashboardFilters(dimensions, widgets, fallbackFilters)
  if (!dashboardFilters.length) {
    return null
  }

  const dateFilter = dashboardFilters.find((filter) => String(filter.type).toLowerCase() === 'date' || normalizeToken(filter.name).includes('date'))
  const slicers = dashboardFilters
    .filter((filter) => filter.value !== dateFilter?.value)
    .map((filter) => ({
      field: filter.value,
      label: formatFieldDisplayName(filter.name),
      control: 'dropdown' as const,
      default: null,
    }))

  return {
    position: 'top',
    clearAll: true,
    dateRange: dateFilter ? { field: dateFilter.value, showFromTo: true } : undefined,
    slicers,
  }
}

function getRequestedSpecialWidgetTypes(query: string): DashboardWidgetType[] {
  const lower = String(query ?? '').toLowerCase()
  const requested: DashboardWidgetType[] = []

  if (/(pivot|matrix)/.test(lower)) {
    requested.push('pivotTable')
  }

  if (/(data bar|bar table|table bars)/.test(lower)) {
    requested.push('dataBarTable')
  }

  return requested
}

function buildWidget(
  type: DashboardWidgetType,
  intent: (typeof INTENTS)[number],
  dimensions: BuilderFieldOption[],
  measures: BuilderFieldOption[],
  filters: DashboardWidgetFilter[],
  index: number,
): DashboardWidget {
  const selectedDimensions = selectDimensionsForType(type, dimensions)
  const selectedMeasures = selectMeasuresForType(type, measures)
  const effectiveFilters = type === 'insight' ? [] : filters
  const title = buildWidgetTitle(type, intent, selectedDimensions, index)

  const baseWidget: DashboardWidget = {
    id: `${intent.key}-${slug(type)}-${index + 1}`,
    type,
    size: TYPE_SIZES[type] ?? 'half',
    title,
    description: buildWidgetDescription(type, title, selectedDimensions, selectedMeasures),
    dimensions: selectedDimensions,
    measures: selectedMeasures,
    filters: effectiveFilters,
    daxGenerationRequest: type === 'insight'
      ? { dimensions: [], measures: [], filters: [] }
      : buildDaxGenerationRequest(selectedDimensions, selectedMeasures, effectiveFilters),
    validation: [],
    availableTypes: type === 'insight' ? ['insight'] : [...EDITABLE_WIDGET_TYPES],
  }

  if (type === 'dataBarTable') {
    return {
      ...baseWidget,
      rowDimension: selectedDimensions[0]?.value ?? null ?? undefined,
      columns: selectedMeasures.map((measure) => ({
        measure: measure.value,
        label: formatFieldDisplayName(measure.name),
        bar: true,
      })),
      showBars: true,
      zebra: true,
    }
  }

  if (type === 'pivotTable') {
    return {
      ...baseWidget,
      rowDimension: selectedDimensions[0]?.value ?? null ?? undefined,
      rowFields: selectedDimensions[0] ? [selectedDimensions[0].value] : [],
      columnFields: selectedDimensions[1] ? [selectedDimensions[1].value] : [],
      valueMeasures: selectedMeasures.map((measure) => measure.value),
      showTotals: true,
      zebra: true,
    }
  }

  if (type === 'gauge') {
    return {
      ...baseWidget,
      ...resolveGaugeMeasureFields(baseWidget, selectedMeasures),
    }
  }

  if (type === 'kpi' && selectedMeasures.length > 1) {
    return {
      ...baseWidget,
      cards: selectedMeasures.map((measure, measureIndex) => ({
        measure: measure.value,
        label: formatFieldDisplayName(measure.name),
        lead: measureIndex === 0,
      })),
    }
  }

  return baseWidget
}

function buildWidgetSet(intent: (typeof INTENTS)[number], dimensions: BuilderFieldOption[], measures: BuilderFieldOption[], query: string) {
  const filters = buildFilter(query, dimensions)
  const requestedSpecialTypes = getRequestedSpecialWidgetTypes(query)
  const widgetPlan = [...intent.widgetPlan]
  requestedSpecialTypes.forEach((type) => {
    const tableIndex = widgetPlan.findIndex((widgetType) => widgetType === 'table')
    if (tableIndex >= 0) {
      widgetPlan[tableIndex] = type
    } else if (!widgetPlan.includes(type)) {
      widgetPlan.push(type)
    }
  })

  return widgetPlan.map((type, index) => buildWidget(type, intent, dimensions, measures, filters, index))
}

function buildDashboardFilters(dimensions: BuilderFieldOption[], widgets: DashboardWidget[], fallbackFilters: DashboardWidgetFilter[]) {
  const dimensionCounts = new Map<string, number>()
  for (const widget of widgets) {
    for (const dimension of widget.dimensions ?? []) {
      dimensionCounts.set(dimension.value, (dimensionCounts.get(dimension.value) ?? 0) + 1)
    }
  }

  const autoFilters = dimensions
    .filter((dimension) => (dimensionCounts.get(dimension.value) ?? 0) >= 2)
    .map((dimension) => ({
      name: dimension.name,
      value: dimension.value,
      type: dimension.type,
    }))

  if (autoFilters.length > 0) {
    return autoFilters
  }

  return fallbackFilters.map((filter) => ({
    name: filter.fieldName,
    value: filter.fieldValue,
    type: filter.type,
  }))
}

function buildWarnings(query: string, dimensions: BuilderFieldOption[], measures: BuilderFieldOption[]) {
  const warnings: string[] = []
  if (dimensions.length === 0) {
    warnings.push('No matching dimensions were found in the PracIntel catalog.')
  }

  if (measures.length === 0) {
    warnings.push('No matching measures were found in the PracIntel catalog.')
  }

  const queryTerms = String(query ?? '').toLowerCase()
  if (queryTerms.includes('spaceship') || queryTerms.includes('unicorn') || queryTerms.includes('crypto')) {
    warnings.push('Some requested concepts do not exist in the saved PracIntel catalog and were ignored.')
  }

  return warnings
}

function ensureMinimumWidgets(
  widgets: DashboardWidget[],
  intent: (typeof INTENTS)[number],
  dimensions: BuilderFieldOption[],
  measures: BuilderFieldOption[],
  filters: DashboardWidgetFilter[],
) {
  if (widgets.length >= 4) {
    return widgets
  }

  const fillerTypes: DashboardWidgetType[] = ['kpi', 'bar', 'line', 'table']
  const nextWidgets = [...widgets]
  for (const type of fillerTypes) {
    if (nextWidgets.some((widget) => widget.type === type)) {
      continue
    }

    nextWidgets.push(buildWidget(type, intent, dimensions, measures, filters, nextWidgets.length))
    if (nextWidgets.length >= 4) {
      break
    }
  }

  return nextWidgets
}

/**
 * Gauge actual/target bindings. Configured bindings are kept as authored; any
 * missing ones are filled, in order, from the selected measures not already bound.
 */
function resolveGaugeMeasureFields(
  widget: DashboardWidget,
  selectedMeasures: BuilderFieldOption[],
): Pick<DashboardWidget, 'actualMeasure' | 'targetMeasure'> {
  const bound = [widget.actualMeasure, widget.targetMeasure]
  const boundTokens = new Set(bound.filter(Boolean).map((value) => normalizeToken(value)))
  const unbound = selectedMeasures
    .filter((measure) => !boundTokens.has(normalizeToken(measure.value)) && !boundTokens.has(normalizeToken(measure.name)))
    .map((measure) => measure.value)
  const [actualMeasure, targetMeasure] = bound.map((value) => value || unbound.shift())
  return { actualMeasure, targetMeasure }
}

/** `orientation` belongs to bars, so a widget changed to another type starts across if it's changed back. */
function withBarOrientation(widget: DashboardWidget): DashboardWidget {
  if (widget.orientation === undefined || widget.type === 'bar' || widget.type === 'groupedBar') return widget
  const { orientation: _orientation, ...rest } = widget
  return rest
}

function normalizeWidget(widget: DashboardWidget, catalog: DashboardCatalogPlatform[], intent: (typeof INTENTS)[number], query = ''): DashboardWidget {
  const flattened = flattenCatalog(catalog)
  const dimensions = uniqueByValue(
    (widget.dimensions ?? []).map((dimension) => findOption(flattened.dimensions, dimension.value || dimension.name)),
  )
  const measures = uniqueByValue(
    (widget.measures ?? []).map((measure) => findOption(flattened.measures, measure.value || measure.name)),
  )
  const filters = Array.isArray(widget.filters) ? widget.filters : buildFilter(query, dimensions)

  if (widget.type === 'insight') {
    return {
      ...withBarOrientation(widget),
      size: widget.size ?? deriveWidgetSize(widget),
      description: widget.description || buildWidgetDescription('insight', widget.title || intent.title, [], []),
      dimensions: [],
      measures: [],
      filters: [],
      daxGenerationRequest: { dimensions: [], measures: [], filters: [] },
      availableTypes: ['insight'],
    }
  }

  const selectedDimensions = dimensions.length ? dimensions : selectDimensionsForType(widget.type, flattened.dimensions)
  const selectedMeasures = widget.type === 'line'
    ? selectMeasuresForType(widget.type, measures.length ? measures : flattened.measures)
    : measures.length ? measures : selectMeasuresForType(widget.type, flattened.measures)

  return {
    ...withBarOrientation(widget),
    ...(widget.type === 'gauge' ? resolveGaugeMeasureFields(widget, selectedMeasures) : {}),
    size: widget.size ?? deriveWidgetSize(widget),
    title: widget.title || buildWidgetTitle(widget.type, intent, selectedDimensions, 0),
    description: widget.description || buildWidgetDescription(widget.type, widget.title || intent.title, selectedDimensions, selectedMeasures),
    dimensions: selectedDimensions,
    measures: selectedMeasures,
    filters,
    daxGenerationRequest: buildDaxGenerationRequest(selectedDimensions, selectedMeasures, filters),
    availableTypes: [...EDITABLE_WIDGET_TYPES],
    cards: widget.cards ?? (widget.type === 'kpi' && selectedMeasures.length > 1
      ? selectedMeasures.map((measure, index) => ({
          measure: measure.value,
          label: formatFieldDisplayName(measure.name),
          lead: index === 0,
        }))
      : undefined),
    columns: widget.columns ?? (widget.type === 'dataBarTable'
      ? selectedMeasures.map((measure) => ({
          measure: measure.value,
          label: formatFieldDisplayName(measure.name),
          bar: true,
        }))
      : undefined),
    rowDimension: widget.rowDimension ?? ((widget.type === 'dataBarTable' || widget.type === 'pivotTable') ? selectedDimensions[0]?.value : undefined),
    rowFields: widget.rowFields ?? (widget.type === 'pivotTable' && selectedDimensions[0] ? [selectedDimensions[0].value] : undefined),
    columnFields: widget.columnFields ?? (widget.type === 'pivotTable' && selectedDimensions[1] ? [selectedDimensions[1].value] : undefined),
    valueMeasures: widget.valueMeasures ?? ((widget.type === 'pivotTable' || widget.type === 'dataBarTable') ? selectedMeasures.map((measure) => measure.value) : undefined),
    showTotals: widget.showTotals ?? (widget.type === 'pivotTable' ? true : undefined),
    showBars: widget.showBars ?? (widget.type === 'dataBarTable' ? true : undefined),
    zebra: widget.zebra ?? ((widget.type === 'pivotTable' || widget.type === 'dataBarTable') ? true : undefined),
  }
}

function buildDashboardShape(
  intent: (typeof INTENTS)[number],
  flattened: ReturnType<typeof flattenCatalog>,
  dimensions: BuilderFieldOption[],
  measures: BuilderFieldOption[],
  query: string,
): DashboardDefinition {
  const filters = buildFilter(query, dimensions)
  const plannedWidgets = buildWidgetSet(intent, dimensions, measures, query)
  const widgets = ensureMinimumWidgets(plannedWidgets, intent, dimensions, measures, filters)

  return {
    version: DASHBOARD_VERSION,
    intentKey: intent.key,
    platformType: flattened.platformType,
    platformTypeId: flattened.platformTypeId,
    title: intent.title,
    summary: intent.summary,
    widgets,
    dashboardFilters: buildDashboardFilters(dimensions, widgets, filters),
    slicerPanel: buildSlicerPanel(dimensions, widgets, filters),
    warnings: buildWarnings(query, dimensions, measures),
  }
}

export function buildDashboardFromQuery(query: string, catalog: DashboardCatalogPlatform[]) {
  const flattened = flattenCatalog(catalog)
  const intent = detectIntent(query)
  const requestedDimensions = extractRequestedFields(query, flattened.dimensions)
  const requestedMeasures = extractRequestedFields(query, flattened.measures)
  const intentDimensions = intent.dimensions.map((name) => findOption(flattened.dimensions, name))
  const intentMeasures = intent.measures.map((name) => findOption(flattened.measures, name))
  const dimensions = uniqueByValue([...requestedDimensions, ...intentDimensions]).slice(0, 6)
  const measures = uniqueByValue([...requestedMeasures, ...intentMeasures]).slice(0, 6)

  return buildDashboardShape(intent, flattened, dimensions, measures, query)
}

function changeWidgetType(widget: DashboardWidget, nextType: DashboardWidgetType, dashboard: DashboardDefinition, catalog: DashboardCatalogPlatform[]) {
  if (widget.type === 'insight' || nextType === 'insight') {
    return normalizeWidget({ ...widget, type: nextType }, catalog, inferDashboardIntent(dashboard), dashboard.title)
  }

  const normalized = normalizeWidget({ ...widget, type: nextType }, catalog, inferDashboardIntent(dashboard), dashboard.title)
  normalized.title = buildWidgetTitle(nextType, inferDashboardIntent(dashboard), normalized.dimensions, 0)
  normalized.description = buildWidgetDescription(nextType, normalized.title, normalized.dimensions, normalized.measures)
  return normalized
}

function parseWidgetTarget(instruction: string, widgets: DashboardWidget[]) {
  const lower = String(instruction ?? '').toLowerCase()
  for (const [ordinal, index] of Object.entries(ORDINAL_INDEX)) {
    if (lower.includes(`${ordinal} widget`) || lower.includes(`${ordinal} chart`) || lower.includes(`${ordinal} visual`)) {
      return widgets[index] ?? null
    }
  }

  if (lower.includes('last widget') || lower.includes('last chart') || lower.includes('last visual')) {
    return widgets.at(-1) ?? null
  }

  return widgets.find((widget) => lower.includes(widget.id.toLowerCase()))
    ?? widgets.find((widget) => lower.includes(widget.title.toLowerCase()))
    ?? widgets.find((widget) => lower.includes(widget.type.toLowerCase()))
    ?? null
}

function parseTypeMention(instruction: string, allowInsight = false): DashboardWidgetType | null {
  const lower = String(instruction ?? '').toLowerCase()
  if (/(trend|timeline|over time)/.test(lower)) {
    return 'line'
  }

  if (/(mix|composition|share|split)/.test(lower)) {
    return 'donut'
  }

  if (/(detail|drill|breakdown table|rows)/.test(lower)) {
    return 'table'
  }

  if (/(pivot|matrix)/.test(lower)) {
    return 'pivotTable'
  }

  if (/(data bar|bar table|table bars)/.test(lower)) {
    return 'dataBarTable'
  }

  if (/(scorecard|headline|summary kpi|kpis)/.test(lower)) {
    return 'kpi'
  }

  const types: DashboardWidgetType[] = allowInsight ? [...EDITABLE_WIDGET_TYPES, 'insight'] : [...EDITABLE_WIDGET_TYPES]
    .slice()
    .sort((left, right) => {
      const leftToken = left === 'groupedBar' ? 'grouped bar' : left
      const rightToken = right === 'groupedBar' ? 'grouped bar' : right
      return rightToken.length - leftToken.length
    })

  const matchedType = types.find((type) => {
    const token = type === 'groupedBar' ? 'grouped bar' : type
    return lower.includes(token)
  })

  return matchedType ?? null
}

function addWidgetOfType(dashboard: DashboardDefinition, nextType: DashboardWidgetType, catalog: DashboardCatalogPlatform[]) {
  const intent = inferDashboardIntent(dashboard)
  const sourceWidget = dashboard.widgets.find((widget) => widget.type !== 'insight') ?? buildWidget(nextType, intent, [], [], [], 0)
  const draft: DashboardWidget = {
    ...sourceWidget,
    id: `${intent.key}-${slug(nextType)}-${dashboard.widgets.length + 1}`,
    type: nextType,
  }
  return changeWidgetType(draft, nextType, dashboard, catalog)
}

function withDashboardWarnings(dashboard: DashboardDefinition, warnings: string[]): DashboardDefinition {
  return {
    ...dashboard,
    warnings: [...new Set([...(dashboard.warnings ?? []), ...warnings])],
  }
}

/**
 * Entries of a widget's `drillDownDimensions` that are not in the catalog.
 * Non-blocking: the array is kept as authored (never pruned) so the drill
 * resolver can disable the widget's drill instead of drilling a silently
 * shortened path.
 */
export function getUnknownDrillDownDimensions(widget: DashboardWidget, catalog: DashboardCatalogPlatform[]) {
  const entries = widget.drillDownDimensions ?? []
  if (!entries.length) return []
  const dimensions = flattenCatalog(catalog).dimensions
  return entries.filter((entry) => !findOption(dimensions, entry))
}

function getDrillDownDimensionWarnings(widgets: DashboardWidget[], catalog: DashboardCatalogPlatform[]) {
  return widgets.flatMap((widget) => {
    const unknown = getUnknownDrillDownDimensions(widget, catalog)
    return unknown.length
      ? [`Drilldown is disabled for ${widget.id}: unknown drillDownDimensions ${unknown.join(', ')}.`]
      : []
  })
}

function rehydrateDashboard(dashboard: DashboardDefinition, catalog: DashboardCatalogPlatform[]) {
  const intent = inferDashboardIntent(dashboard)
  const widgets = (dashboard.widgets ?? []).map((widget) => normalizeWidget(widget, catalog, intent, dashboard.title))
  const drillWarnings = getDrillDownDimensionWarnings(widgets, catalog)
  const fallbackFilters = uniqueByValue(
    widgets
      .flatMap((widget) => widget.filters ?? [])
      .map((filter) => ({
        name: filter.fieldName,
        value: filter.fieldValue,
        type: filter.type,
      })),
  )

  return {
    ...dashboard,
    ...(drillWarnings.length ? { warnings: [...new Set([...(dashboard.warnings ?? []), ...drillWarnings])] } : {}),
    version: DASHBOARD_VERSION,
    intentKey: intent.key,
    widgets,
    dashboardFilters: buildDashboardFilters(
      uniqueByValue(widgets.flatMap((widget) => widget.dimensions ?? [])),
      widgets,
      fallbackFilters.map((filter) => ({
        fieldName: filter.name,
        fieldValue: filter.value,
        type: filter.type,
        operator: '=',
        value: filter.value,
      })),
    ),
    slicerPanel: dashboard.slicerPanel ?? buildSlicerPanel(
      uniqueByValue(widgets.flatMap((widget) => widget.dimensions ?? [])),
      widgets,
      fallbackFilters.map((filter) => ({
        fieldName: filter.name,
        fieldValue: filter.value,
        type: filter.type,
        operator: '=',
        value: filter.value,
      })),
    ),
  }
}

export function normalizeDashboardDefinition(dashboard: DashboardDefinition, catalog: DashboardCatalogPlatform[]) {
  const nextDashboard = cloneJson(dashboard)
  return rehydrateDashboard(nextDashboard, catalog)
}

export function applyDashboardEdit(dashboard: DashboardDefinition, instruction: string, catalog: DashboardCatalogPlatform[]) {
  const nextDashboard = cloneJson(dashboard)
  const lower = String(instruction ?? '').toLowerCase().trim()
  if (!lower) {
    return withDashboardWarnings(nextDashboard, ['No edit instruction was provided.'])
  }

  if (!nextDashboard.widgets?.length) {
    return withDashboardWarnings(nextDashboard, ['There is no existing dashboard content to edit yet.'])
  }

  const targetType = parseTypeMention(lower)
  const targetWidget = parseWidgetTarget(lower, nextDashboard.widgets)

  if ((lower.includes('remove') || lower.includes('delete')) && targetWidget) {
    nextDashboard.widgets = nextDashboard.widgets.filter((widget) => widget.id !== targetWidget.id)
    return rehydrateDashboard(withDashboardWarnings(nextDashboard, [`Removed ${targetWidget.title}.`]), catalog)
  }

  if ((lower.includes('add') || lower.includes('insert')) && targetType) {
    nextDashboard.widgets.push(addWidgetOfType(nextDashboard, targetType, catalog))
    return rehydrateDashboard(withDashboardWarnings(nextDashboard, [`Added a ${TYPE_LABELS[targetType]} widget.`]), catalog)
  }

  const replacementType = /(?:change|swap|replace|turn)\b/.test(lower) ? parseTypeMention(lower) : null
  if (targetWidget && replacementType) {
    nextDashboard.widgets = nextDashboard.widgets.map((widget) => (
      widget.id === targetWidget.id
        ? changeWidgetType(widget, replacementType, nextDashboard, catalog)
        : widget
    ))
    return rehydrateDashboard(withDashboardWarnings(nextDashboard, [`Changed ${targetWidget.title} to ${TYPE_LABELS[replacementType]}.`]), catalog)
  }

  if ((lower.includes('trend') || lower.includes('timeline')) && !targetWidget) {
    const candidate = nextDashboard.widgets.find((widget) => widget.type === 'bar' || widget.type === 'groupedBar' || widget.type === 'donut')
      ?? nextDashboard.widgets.find((widget) => widget.type !== 'insight')
    if (candidate) {
      nextDashboard.widgets = nextDashboard.widgets.map((widget) => (
        widget.id === candidate.id ? changeWidgetType(widget, 'line', nextDashboard, catalog) : widget
      ))
      return rehydrateDashboard(withDashboardWarnings(nextDashboard, [`Changed ${candidate.title} to Line.`]), catalog)
    }
  }

  if ((lower.includes('composition') || lower.includes('mix') || lower.includes('share')) && !targetWidget) {
    nextDashboard.widgets.push(addWidgetOfType(nextDashboard, 'donut', catalog))
    return rehydrateDashboard(withDashboardWarnings(nextDashboard, ['Added a Donut widget for composition analysis.']), catalog)
  }

  if ((lower.includes('detail') || lower.includes('drill')) && !targetWidget) {
    if (!nextDashboard.widgets.some((widget) => widget.type === 'table')) {
      nextDashboard.widgets.push(addWidgetOfType(nextDashboard, 'table', catalog))
      return rehydrateDashboard(withDashboardWarnings(nextDashboard, ['Added a Table widget for drill-down detail.']), catalog)
    }
  }

  if (lower.includes('more charts') || lower.includes('more visuals') || lower.includes('more widgets')) {
    for (const fillerType of ['kpi', 'groupedBar', 'line', 'table'] as DashboardWidgetType[]) {
      if (nextDashboard.widgets.some((widget) => widget.type === fillerType)) {
        continue
      }
      nextDashboard.widgets.push(addWidgetOfType(nextDashboard, fillerType, catalog))
      if (nextDashboard.widgets.length >= 5) {
        break
      }
    }
    return rehydrateDashboard(withDashboardWarnings(nextDashboard, ['Expanded the dashboard with additional visual types.']), catalog)
  }

  return withDashboardWarnings(nextDashboard, ['The edit instruction was not understood. Try changing, adding, or removing a widget type.'])
}

export function validateDashboard(dashboard: DashboardDefinition, catalog: DashboardCatalogPlatform[]) {
  const flattened = flattenCatalog(catalog)
  const dimensionValues = new Set(flattened.dimensions.map((dimension) => dimension.value))
  const measureValues = new Set(flattened.measures.map((measure) => measure.value))
  const errors: string[] = []

  if (dashboard.version !== DASHBOARD_VERSION && dashboard.version !== LEGACY_DASHBOARD_VERSION) {
    errors.push('Invalid version.')
  }

  for (const widget of dashboard.widgets ?? []) {
    if (!TYPE_LABELS[widget.type]) {
      errors.push(`Unsupported widget type ${widget.type} in ${widget.id}.`)
    }

    for (const dimension of widget.dimensions ?? []) {
      if (!dimensionValues.has(dimension.value)) {
        errors.push(`Invalid dimension ${dimension.value} in ${widget.id}.`)
      }
    }

    for (const measure of widget.measures ?? []) {
      if (!measureValues.has(measure.value)) {
        errors.push(`Invalid measure ${measure.value} in ${widget.id}.`)
      }
    }

    if (widget.type !== 'insight' && !(widget.daxGenerationRequest?.measures?.length > 0)) {
      errors.push(`Missing daxGenerationRequest for ${widget.id}.`)
    }

    if (widget.rowDimension && !dimensionValues.has(widget.rowDimension)) {
      errors.push(`Invalid rowDimension ${widget.rowDimension} in ${widget.id}.`)
    }

    for (const dimension of widget.drillDimensions ?? []) {
      if (!dimensionValues.has(dimension)) {
        errors.push(`Invalid drill dimension ${dimension} in ${widget.id}.`)
      }
    }

    // Non-blocking by design (see getUnknownDrillDownDimensions): surfaced as
    // a dashboard warning in rehydrateDashboard, not a validation error.

    for (const field of [...(widget.rowFields ?? []), ...(widget.columnFields ?? [])]) {
      if (!dimensionValues.has(field)) {
        errors.push(`Invalid pivot field ${field} in ${widget.id}.`)
      }
    }

    for (const measure of [...(widget.valueMeasures ?? []), ...(widget.hiddenMeasures ?? [])]) {
      if (!measureValues.has(measure)) {
        errors.push(`Invalid widget measure ${measure} in ${widget.id}.`)
      }
    }
  }

  const slicerPanel = dashboard.slicerPanel
  if (slicerPanel) {
    for (const slicer of slicerPanel.slicers ?? []) {
      if (!dimensionValues.has(slicer.field)) {
        errors.push(`Invalid slicer field ${slicer.field}.`)
      }
    }

    if (slicerPanel.dateRange?.field && !dimensionValues.has(slicerPanel.dateRange.field)) {
      errors.push(`Invalid slicer date field ${slicerPanel.dateRange.field}.`)
    }

    const dateRangeMode = slicerPanel.dateRange?.mode
    if (dateRangeMode !== undefined && dateRangeMode !== 'preset' && dateRangeMode !== 'custom' && dateRangeMode !== 'none') {
      errors.push(`Invalid slicer date range mode ${String(dateRangeMode)}; expected preset, custom or none.`)
    }

    if (dateRangeMode === 'preset' && !String(slicerPanel.dateRange?.preset ?? '').trim()) {
      errors.push('Missing slicer date range preset; mode preset requires a preset.')
    }
  }

  return { ok: errors.length === 0, errors }
}
